import { Box, Stack, Typography, useTheme } from '@mui/material';
import { BaseTheme } from 'design-system';
import { useInputsBuyerInfo } from 'providers/InputsBuyerInfoProvider';
import { useMemo } from 'react';
import useButtonColor from 'hooks/useButtonColor';
import useIsDarkMode from 'hooks/useIsDarkMode';
import { useIsFocusHeaderPath } from 'hooks/useIsFocusHeaderPath';
import useIsViewport from 'hooks/useIsViewport';
import { ButtonColor } from 'generated/graphql';

const verticalBarSx = (theme: BaseTheme, isDarkMode: boolean) => ({
  width: '1px',
  height: '25px',
  bgcolor: isDarkMode ? theme.palette.common.white : theme.palette.grey[300],
  opacity: isDarkMode ? 0.25 : undefined,
});

const CreditBalance = ({ color }: { color?: ButtonColor }) => {
  const theme = useTheme();
  const isMobile = useIsViewport();
  const { buyerInfo, isFetching } = useInputsBuyerInfo();
  const isDarkMode = useIsDarkMode();
  const isFocusHeader = useIsFocusHeaderPath();
  const hasBorders = !isMobile && !isFocusHeader;

  const creditBalance = useMemo(() => buyerInfo?.creditBalance, [buyerInfo]);
  const textColor = useButtonColor(theme, color) ?? 'inherit';

  if (!creditBalance && !isFetching) {
    return null;
  }

  return (
    <Stack
      direction="row"
      sx={{
        alignItems: 'center',
      }}
    >
      {hasBorders && (
        <Box
          sx={{
            mr: theme.spacing(0.5),
            ...verticalBarSx(theme, isDarkMode),
          }}
        />
      )}
      <Stack
        sx={{
          alignItems: 'center',
          justifyContent: 'center',

          [theme.breakpoints.up('md')]: {
            px: theme.spacing(2),
          },
        }}
      >
        <Typography variant="body2" color={textColor}>
          Credit Balance
        </Typography>
        <Typography
          color={textColor}
          sx={{
            fontWeight: theme.typography.fontWeightMedium,
          }}
        >
          {creditBalance ?? 'R0.00'}
        </Typography>
      </Stack>
      {hasBorders && (
        <Box
          sx={{
            ml: theme.spacing(0.5),
            mr: theme.spacing(1.5),
            ...verticalBarSx(theme, isDarkMode),
          }}
        />
      )}
    </Stack>
  );
};

export default CreditBalance;
